import { Marker, MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import {
  APIProvider,
  AdvancedMarker,
  ControlPosition,
  InfoWindow,
  Map,
  MapControl,
  useAdvancedMarkerRef,
  useMap,
} from '@vis.gl/react-google-maps';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RotateLoader } from 'react-spinners';
import { useNotify } from '../Common/snackbarHooks';
import { stringToPositiveInt, useQuery } from '../Common/utilities';
import { getResidencePoints } from './residenceApi';
import { ResidencePointInterface } from './types';
import ResidenceIcon from './components/ResidenceIcon';
import FormAutocompleteBox from '../Common/FormItems/FormAutocompleteBox';
import { Button } from '@mui/material';

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY ? process.env.REACT_APP_GOOGLE_MAPS_KEY : '';
const DEFAULT_CENTER = { lat: 59.3293, lng: 18.0686 };
const DEFAULT_ZOOM = 10;

export default function ResidenceMap() {
  const [values] = useQuery({
    center: (val) => stringToPositiveInt(val),
    complete: (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
  });
  const { notifyError } = useNotify();
  const [residencePoints, setResidencePoints] = useState<ResidencePointInterface[]>();
  const center = useMemo(() => {
    return residencePoints?.find(({ id }) => id === values.center);
  }, [residencePoints]);

  useEffect(() => {
    getResidencePoints()
      .then((data) => setResidencePoints(data.data.residences))
      .catch(() => notifyError('Det gick inte att hämta listan över boenden'));
  }, []);

  return (
    <APIProvider apiKey={API_KEY}>
      {residencePoints ? (
        <Map
          mapId={'b3831a9c8a09d3b1b77eaf75'}
          style={{ width: '100vw', height: '100vh' }}
          defaultCenter={center?.point ?? DEFAULT_CENTER}
          defaultZoom={DEFAULT_ZOOM * (center ? 2.5 : 1)}
          streetViewControl={false}
          mapTypeControl={false}
          reuseMaps={true}
        >
          {values.complete && <AutocompleControl points={residencePoints} />}
          <FindMyLocationControl />
          <Markers points={residencePoints} showInfo={values.complete} />
        </Map>
      ) : (
        <div className="fixed top-1/2 left-1/2">
          <RotateLoader />
        </div>
      )}
    </APIProvider>
  );
}

function AutocompleControl({ points }: Readonly<{ points: ResidencePointInterface[] }>) {
  const map = useMap();

  return (
    <MapControl position={ControlPosition.TOP}>
      <FormAutocompleteBox
        type="single"
        placeholder="Sök boende"
        options={points}
        value={null}
        onChange={(_, option) => {
          if (!option || !map) return;
          map.setCenter(option.point);
          map.setZoom(DEFAULT_ZOOM * 1.5);
        }}
        className="!mt-6 !w-64 !bg-white !rounded"
      />
    </MapControl>
  );
}

function FindMyLocationControl() {
  const map = useMap();
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [infoWindowMessage, setInfoWindowMessage] = useState('');

  const handleLocationError = (browserHasGeolocation: boolean) => {
    setInfoWindowMessage(
      browserHasGeolocation
        ? 'Error: Browser geolocation is disabled. Enable browser geolocation on your system settings and try again. '
        : "Error: Your browser doesn't support geolocation."
    );
    setInfoWindowOpen(true);
  };

  return (
    <>
      <MapControl position={ControlPosition.BOTTOM}>
        <Button
          color="primary"
          variant="contained"
          fullWidth
          className="!mb-6 !rounded"
          onClick={(): void => {
            if (!map) return;
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position: GeolocationPosition) => {
                  const pos = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  };
                  map.setCenter(pos);
                },
                () => {
                  handleLocationError(true);
                }
              );
            } else {
              // Browser doesn't support Geolocation
              handleLocationError(false);
            }
          }}
        >
          Hitta mig
        </Button>
      </MapControl>
      {infoWindowOpen && (
        <InfoWindow position={map?.getCenter()} maxWidth={200} onCloseClick={() => setInfoWindowOpen(false)}>
          {infoWindowMessage}
        </InfoWindow>
      )}
    </>
  );
}

function Markers({ points, showInfo }: Readonly<{ points: ResidencePointInterface[]; showInfo?: boolean }>) {
  const map = useMap();
  const [markers, setMarkers] = useState<{ [key: number]: Marker }>({});
  const clusterer = useRef<MarkerClusterer | null>(null);
  const [openInfo, setOpenInfo] = useState<number | null>(null);
  const [showLabels, setShowLabels] = useState(false);

  useEffect(() => {
    if (!map) return;
    if (clusterer.current) return;
    clusterer.current = new MarkerClusterer({
      map,
      algorithm: new SuperClusterAlgorithm({
        radius: 50,
        minPoints: 4,
      }),
    });
  }, [map]);

  useEffect(() => {
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [markers]);

  useEffect(() => {
    if (!map) return;
    map.addListener('zoom_changed', () => {
      setShowLabels((map.getZoom() ?? 0) > 13);
    });
  }, [map]);

  const setMarkerRef = (marker: Marker | null, key: number) => {
    if (marker && markers[key]) return;
    if (!marker && !markers[key]) return;

    setMarkers((prev) => {
      if (marker) {
        return { ...prev, [key]: marker };
      } else {
        const newMarkers = { ...prev };
        delete newMarkers[key];
        return newMarkers;
      }
    });
  };

  return (
    <>
      {points.map((point) => (
        <InfoMarker
          key={point.id}
          point={point}
          showInfo={showInfo}
          isOpen={openInfo === point.id}
          setOpenInfo={setOpenInfo}
          setMarkerRef={setMarkerRef}
          showLabel={showLabels}
        />
      ))}
    </>
  );
}

type InfoMarkerProps = {
  point: ResidencePointInterface;
  showInfo?: boolean;
  isOpen: boolean;
  setOpenInfo: (value: number | null) => void;
  setMarkerRef?: (marker: Marker | null, key: number) => void;
  showLabel?: boolean;
};

function InfoMarker({ point, showInfo, isOpen, setOpenInfo, setMarkerRef, showLabel }: Readonly<InfoMarkerProps>) {
  const [markerRef, marker] = useAdvancedMarkerRef();

  return (
    <>
      <AdvancedMarker
        ref={(marker) => {
          setMarkerRef?.(marker, point.id);
          return markerRef(marker);
        }}
        onClick={() => {
          setOpenInfo(point.id);
        }}
        position={{ ...point.point }}
        title={point.name}
      >
        <div className="relative" onMouseOver={() => setOpenInfo(point.id)}>
          {showInfo && showLabel && !isOpen && (
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 font-bold text-center">{point.name}</span>
          )}
          <ResidenceIcon className="text-xl" />
        </div>
      </AdvancedMarker>
      {showInfo && isOpen && (
        <InfoWindow anchor={marker} maxWidth={200} onCloseClick={() => setOpenInfo(null)}>
          <h3 className="text-sm font-medium">{point.name}</h3>
          <p> {point.address}</p>
        </InfoWindow>
      )}
    </>
  );
}
